import React from 'react'
import { StyleSheet, View } from 'react-native'
import { Text, Card, Chip, IconButton } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../../../hooks/theme.hook'
import { ProgressBar } from '../../../../@ui/containers/others/progress-bar'
import { format } from 'date-fns'
import { ListInvitationsQuery } from '../../../../generated/graphql'
import { copyInvitationLink } from '../../../../utils/invitation.utils'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../utils/message-type'
import { DeleteInvitationButton } from './delete-invitation.button'

type InvitationItem =
  ListInvitationsQuery['listInvitations']['invitations'][number]

export interface InvitationCardProps {
  invitation: InvitationItem
  onDeleted?: () => void
}

export function InvitationCard({ invitation, onDeleted }: InvitationCardProps) {
  const { t } = useTranslation('nodes')
  const theme = useTheme()

  const isActive =
    !invitation.maxUsage || invitation.usageCount < invitation.maxUsage
  const usagePercent = invitation.maxUsage
    ? (invitation.usageCount / invitation.maxUsage) * 100
    : 0

  const handleCopyCode = async () => {
    await copyInvitationLink(invitation.code, t('link-copied-to-clipboard'))
  }

  return (
    <Card style={styles.card} mode="outlined">
      <Card.Content>
        <View style={styles.header}>
          <View>
            <Text variant="headlineSmall" style={styles.code}>
              {invitation.code}
            </Text>
            <Text variant="labelSmall" style={styles.date}>
              {format(new Date(invitation.createdAt), 'PPp')}
            </Text>
          </View>

          <Chip
            mode="flat"
            compact
            style={[
              styles.statusChip,
              {
                backgroundColor: isActive
                  ? theme.colors.primaryContainer
                  : theme.colors.surfaceVariant,
              },
            ]}
          >
            {isActive ? t('active') : t('used')}
          </Chip>

          {invitation.maxUsage && (
            <View style={styles.progressContainer}>
              <View style={styles.progressBarWrapper}>
                <ProgressBar
                  percentage={usagePercent}
                  color={
                    usagePercent >= 100
                      ? theme.colors.primary
                      : isActive
                      ? theme.colors.primary
                      : theme.colors.surfaceVariant
                  }
                />
              </View>
              <Text variant="bodySmall" style={styles.usageText}>
                {invitation.usageCount}/{invitation.maxUsage}
              </Text>
            </View>
          )}

          <View style={styles.actions}>
            <IconButton
              icon="content-copy"
              size={20}
              onPress={handleCopyCode}
            />
            <DeleteInvitationButton
              invitationId={invitation.id}
              onDeleted={onDeleted}
            />
          </View>
        </View>

        <View style={styles.footer}>
          {invitation.message && (
            <Text variant="bodyLarge" style={styles.message} numberOfLines={2}>
              {invitation.message}
            </Text>
          )}
        </View>
      </Card.Content>
    </Card>
  )
}

const styles = StyleSheet.create({
  card: {
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  code: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
    letterSpacing: 1,
  },
  statusChip: {
    height: 28,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  progressBarWrapper: {
    flex: 1,
    minHeight: 8,
  },
  usageText: {
    fontSize: 11,
    fontWeight: '600',
    minWidth: 40,
  },
  actions: {
    flexDirection: 'row',
    gap: 4,
    marginLeft: 'auto',
  },
  footer: {
    gap: 8,
  },
  date: {
    opacity: 0.6,
  },
  message: {
    fontWeight: '500',
  },
})
